import { supabase } from "../integrations/supabase/client";
import { SubscriptionTier } from "./SubscriptionService";

export interface SubscriptionData {
  tier_code: SubscriptionTier;
  start_date: Date | null;
  end_date: Date | null;
  is_active: boolean;
  marked_for_cancel?: boolean;
  cancel_at?: Date;
}

export class UserSubscriptionDataService {
  private static instance: UserSubscriptionDataService;
  private subscriptionData: SubscriptionData | null = null;
  private subscribers: ((data: SubscriptionData | null) => void)[] = [];

  private constructor() {
    // Initialize by fetching subscription data
    this.refreshSubscriptionData();
  }

  public static getInstance(): UserSubscriptionDataService {
    if (!UserSubscriptionDataService.instance) {
      UserSubscriptionDataService.instance = new UserSubscriptionDataService();
    }
    return UserSubscriptionDataService.instance;
  }

  public async refreshSubscriptionData(): Promise<SubscriptionData | null> {
    try {
      // First check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser();
      
      // If no authenticated user, return null early
      if (!user) {
        console.log("No authenticated user, skipping subscription data fetch");
        return null;
      }

      const user_id = user.id;

      const { data: user_subscription, error: user_subscription_error } =
        await supabase
          .from("user_subscription")
          .select(`*, subscription_tier(*)`)
          .eq("user_id", user_id)
          .limit(1)
          .single();

      if (user_subscription_error) {
        console.error("Error fetching subscription data:", user_subscription_error);
        return null;
      }

      if (!user_subscription) {
        return null;
      }

      this.subscriptionData = {
        tier_code: user_subscription.subscription_tier.tier_code as SubscriptionTier,
        start_date: user_subscription.current_period_start  ? new Date(user_subscription.current_period_start) : null,
        end_date: user_subscription.current_period_end ? new Date(user_subscription.current_period_end) : null,
        is_active: user_subscription.subscription_tier.tier_code !== "Registered",
        marked_for_cancel: user_subscription.marked_for_cancel,
        cancel_at: user_subscription.cancel_at ? new Date(user_subscription.cancel_at) : undefined
      };

      this.notifySubscribers();
      return this.subscriptionData;
    } catch (error) {
      console.error("Error fetching subscription data:", error);
      return null;
    }
  }

  public getSubscriptionData(): SubscriptionData | null {
    return this.subscriptionData;
  }

  // Subscribe to changes
  public subscribe(
    callback: (data: SubscriptionData | null) => void,
  ): () => void {
    this.subscribers.push(callback);
    callback(this.subscriptionData);

    return () => {
      this.subscribers = this.subscribers.filter((cb) => cb !== callback);
    };
  }

  // Notify all subscribers of changes
  private notifySubscribers(): void {
    for (const callback of this.subscribers) {
      callback(this.subscriptionData);
    }
  }
}

export const userSubscriptionDataService = UserSubscriptionDataService.getInstance();